import { defineStore } from "pinia";
import { api, setCsrfToken } from "../api";

export type Principal = { actorId: string; displayName: string; role: string; tenantIds?: string[] };
export type TenantSummary = {
  tenant_id: string;
  display_name: string;
  status: string;
  primary_domain: string | null;
  connections: Array<{ siteId: string; status: string; lastEventAt: string | null }>;
};

export const useSessionStore = defineStore("session", {
  state: () => ({
    principal: null as Principal | null,
    tenants: [] as TenantSummary[],
    tenantId: "",
    initialized: false,
  }),
  getters: {
    currentTenant(state): TenantSummary | null {
      return state.tenants.find((tenant) => tenant.tenant_id === state.tenantId) ?? null;
    },
  },
  actions: {
    async initialize() {
      try {
        const result = await api<{ principal: Principal; csrfToken?: string }>("/v1/admin/me");
        this.principal = result.principal;
        await this.loadTenants();
      } catch {
        this.principal = null;
        this.tenants = [];
      } finally {
        this.initialized = true;
      }
    },
    async login(token: string) {
      const result = await api<{ principal: Principal; csrfToken: string }>("/v1/admin/session", {
        method: "POST",
        body: JSON.stringify({ token }),
      });
      this.principal = result.principal;
      setCsrfToken(result.csrfToken);
      await this.loadTenants();
    },
    async loadTenants() {
      const result = await api<{ tenants: TenantSummary[] }>("/v1/admin/tenants");
      this.tenants = result.tenants;
      const saved = sessionStorage.getItem("aimarket.admin.tenant");
      this.tenantId = this.tenants.some((tenant) => tenant.tenant_id === saved)
        ? saved!
        : this.tenants.some((tenant) => tenant.tenant_id === this.tenantId)
          ? this.tenantId
          : this.tenants[0]?.tenant_id ?? "";
    },
    selectTenant(tenantId: string) {
      if (!this.tenants.some((tenant) => tenant.tenant_id === tenantId)) return;
      this.tenantId = tenantId;
      sessionStorage.setItem("aimarket.admin.tenant", tenantId);
    },
    async logout() {
      await api<void>("/v1/admin/session", { method: "DELETE" });
      this.principal = null;
      this.tenants = [];
      this.tenantId = "";
      setCsrfToken(null);
    },
  },
});
